import { useEffect, useMemo, useRef, useState } from "react";

import { SearchV2CandidateList } from "./candidates/SearchV2CandidateList";
import { SearchV2ChecklistPanel } from "./checklist/SearchV2ChecklistPanel";
import { SearchV2NoKeywordsGate } from "./SearchV2NoKeywordsGate";
import { searchV2ChecklistStorage } from "./checklist/searchV2ChecklistStorage";
import { SearchV2Header } from "./header/SearchV2Header";
import { searchV2FiltersStorage } from "./header/searchV2FiltersStorage";
import {
  searchV2RecentQueriesStorage,
  type RecentSearchV2Request,
} from "./header/searchV2RecentQueriesStorage";
import { type SearchReviewCandidate } from "./searchV2CandidateResult";
import { SearchV2SallyMode } from "./SearchV2SallyMode";
import { SearchSessionPanel } from "./session/SearchSessionPanel";
import { SearchApplicationReviewModal } from "./sheet/SearchApplicationReviewModal";

import { Column } from "@/components";

import { useSearchInterests } from "@/hooks/search/useSearchInterests";
import { useSearchSession } from "@/hooks/search/useSearchSession";
import { searchStore } from "@/stores/search";
import {
  DEFAULT_SEARCH_WEIGHTS,
  MAX_SEARCH_V2_KEYWORDS,
  MS_PER_DAY,
} from "@/lib/constants";
import {
  ACTIONABLE_SALLY_STATES,
  Job,
  SallyState,
  SearchInterest,
  SearchInterestChangeOptions,
  SearchLastDecision,
  SearchV2Filters,
  SearchV2Weights,
} from "@/types";

// Stable fallback while the interests query is pending — an inline `= []`
// default would be a new array every render and thrash downstream memos.
const EMPTY_SEARCH_INTERESTS: SearchInterest[] = [];
// Hidden candidates older than this get surfaced in a warning so they aren't
// silently filtered out of search forever.
const STALE_HIDDEN_AFTER_DAYS = 3;

export const JobSearchV2 = ({
  companyName,
  isAgentActive,
  job,
}: {
  companyName: string;
  isAgentActive: boolean;
  job: Job;
}) => {
  /* React Hooks */
  const {
    deleteSearchInterest,
    searchInterests = EMPTY_SEARCH_INTERESTS,
    upsertSearchInterest,
  } = useSearchInterests(job.id);
  const {
    getSessionProgress,
    goals: searchSessionGoals,
    interestCoins: searchSessionInterestCoins,
    interestCount,
    recordInterest,
    recordInterestCoins,
    recordShortlist,
    session: searchSession,
    shortlists: searchSessionShortlists,
    stats: searchSessionStats,
  } = useSearchSession(job.id);
  const [storedSearchSettings] = useState(() =>
    searchV2FiltersStorage.get(job.id),
  );
  // `filters` is the single source of truth for keywords, title, and pay. The
  // chips under the input are derived from filters.keywords — not separate state.
  const [filters, setFilters] = useState<SearchV2Filters>(() => {
    return (
      storedSearchSettings?.filters ?? {
        includePastSubmitted: false,
        includeRejected: false,
        // Only the job's default search keywords seed the search; the rest are
        // highlight-only and would widen results unintentionally.
        keywords: job.keywords
          .filter((keyword) => keyword.isDefaultSearchKeyword)
          .slice(0, MAX_SEARCH_V2_KEYWORDS)
          .map(({ category, color, keyword }) => ({
            category,
            color,
            keyword,
          })),
        payCeiling: "",
        title: [],
      }
    );
  });
  const [radius, setRadius] = useState("30 mi");
  // Swaps the corpus from "the whole network" to "everyone Sally worked on this
  // job". Deliberately NOT part of `filters`: filters narrow a search and are
  // persisted for 30 days, whereas this replaces what's being searched and
  // should not survive a reload.
  const [isSallyMode, setIsSallyMode] = useState(false);
  // Which Sally outcomes the mode shows. Edited through the Filters dialog, but
  // kept out of `filters` for the same reason as the mode itself.
  const [sallyStates, setSallyStates] = useState<SallyState[]>(
    ACTIONABLE_SALLY_STATES,
  );
  const [weights, setWeights] = useState<SearchV2Weights>(
    storedSearchSettings?.weights ?? DEFAULT_SEARCH_WEIGHTS,
  );
  const [lastDecision, setLastDecision] = useState<SearchLastDecision | null>(
    null,
  );
  // Results stay blocked until the staffer adds keywords or opts in to a
  // semantics-only search. Sally mode has no semantic fallback to warn about.
  const [isNoKeywordsAcknowledged, setIsNoKeywordsAcknowledged] =
    useState(false);
  const { isReviewOpen, reviewResultsByTalentId, setIsReviewOpen } =
    searchStore();
  const [completedChecklistItems, setCompletedChecklistItems] = useState<
    string[]
  >(() => searchV2ChecklistStorage.get(job.id));

  const [recentSearches, setRecentSearches] = useState<RecentSearchV2Request[]>(
    () => searchV2RecentQueriesStorage.get(job.id),
  );
  const isFirstPersistRun = useRef(true);
  const keywordsBeforeSallyMode = useRef<SearchV2Filters["keywords"]>([]);

  // Hidden candidates left hidden past the threshold get surfaced in the
  // checklist panel so they aren't forgotten forever.
  const staleHidden = useMemo(() => {
    const now = Date.now();
    const staleDays = searchInterests
      .filter((interest) => !interest.isInterested)
      .map((interest) =>
        Math.floor((now - new Date(interest.updatedAt).getTime()) / MS_PER_DAY),
      )
      .filter((days) => days >= STALE_HIDDEN_AFTER_DAYS);
    return {
      count: staleDays.length,
      longestDays: staleDays.length ? Math.max(...staleDays) : 0,
    };
  }, [searchInterests]);

  useEffect(() => {
    // Sally mode empties the keyword box on entry and restores it on exit, so
    // everything `filters` holds while it's on is transitional. Persisting it
    // would save the emptied keywords for 30 days — a reload in Sally mode
    // would then drop you back into a normal search with your terms gone — and
    // the restore on exit would log a "recent search" nobody ran.
    if (isSallyMode) {
      return;
    }
    searchV2FiltersStorage.set(job.id, filters, weights);
    // Skip the mount run — restored filters aren't a new search and shouldn't
    // reorder the recent-search history.
    if (isFirstPersistRun.current) {
      isFirstPersistRun.current = false;
      return;
    }
    // Snapshot the full request (filters + tuning) as a recent search; the
    // storage dedupes, so re-renders with the same request are no-ops.
    const hasSearchCriteria =
      filters.keywords.length > 0 || filters.title.length > 0;
    if (hasSearchCriteria) {
      setRecentSearches(
        searchV2RecentQueriesStorage.add(job.id, { filters, weights }),
      );
    }
  }, [filters, isSallyMode, job.id, weights]);

  /* Helpers */
  const parseRadiusMi = (radiusOption: string) =>
    radiusOption === "Everywhere" ? undefined : parseInt(radiusOption, 10);

  // The review queue is the interest rows themselves — one per talent, so no
  // double counting, and it survives reloads. Each row gets its search result
  // when the list has seen it (keyword highlights, candidacy data); otherwise
  // ids alone and the review sheet hydrates the rest itself.
  const interestedInterests = searchInterests.filter(
    (interest) => interest.isInterested,
  );
  const reviewCandidates: SearchReviewCandidate[] = interestedInterests.map(
    (interest) =>
      reviewResultsByTalentId.get(interest.talentId) ?? {
        talentId: interest.talentId,
        userId: interest.talentId,
      },
  );
  const topCandidateCount = interestedInterests.filter(
    (interest) => interest.isTopCandidate,
  ).length;
  const interestedCandidateCount =
    interestedInterests.length - topCandidateCount;
  // Blank/whitespace overrides fall back to the job's own location.
  const locationOverride = filters.location?.trim();
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const searchLocation = locationOverride
    ? locationOverride
    : `${job.city}, ${job.state}`;

  /* Handlers */
  const handleSearchInterestChange = (
    talentId: number,
    isInterested: boolean | null,
    options?: SearchInterestChangeOptions,
  ) => {
    const existingInterest = searchInterests.find(
      (interest) => interest.talentId === talentId,
    );
    if (isInterested === null) {
      if (existingInterest) {
        deleteSearchInterest({
          jobId: job.id,
          reason: options?.reason,
          searchAnalytics: options?.searchAnalytics,
          talentId,
        });
      }
      return;
    }
    upsertSearchInterest(
      {
        isInterested,
        jobId: job.id,
        searchAnalytics: options?.searchAnalytics,
        talentId,
        ...(searchSession && { sessionId: searchSession.id }),
      },
      {
        onSuccess: (upserted) => {
          recordInterestCoins({
            coins: upserted.stafferPetAward?.coinsAwarded ?? 0,
            goalBonusCoins: upserted.stafferPetGoalAward?.coinsAwarded ?? 0,
          });
        },
      },
    );
    // Count only fresh positive interests toward the session.
    if (isInterested && !existingInterest?.isInterested) {
      recordInterest(talentId);
    }
  };

  const handleRecentSearchSelect = (recentSearch: RecentSearchV2Request) => {
    setFilters(recentSearch.filters);
    setWeights(recentSearch.weights);
  };

  const completeChecklistItem = (item: string) => {
    setCompletedChecklistItems(searchV2ChecklistStorage.add(job.id, item));
  };

  const handleFiltersChange = (nextFilters: SearchV2Filters) => {
    const currentKeywords = filters.keywords.map(({ keyword }) => keyword);
    const nextKeywords = nextFilters.keywords.map(({ keyword }) => keyword);
    if (JSON.stringify(currentKeywords) !== JSON.stringify(nextKeywords)) {
      completeChecklistItem("keywords");
    }
    if (
      filters.includePastSubmitted !== nextFilters.includePastSubmitted ||
      filters.includeRejected !== nextFilters.includeRejected ||
      filters.location !== nextFilters.location ||
      filters.payCeiling !== nextFilters.payCeiling ||
      JSON.stringify(filters.title) !== JSON.stringify(nextFilters.title) ||
      JSON.stringify(filters.tenure ?? null) !==
        JSON.stringify(nextFilters.tenure ?? null)
    ) {
      completeChecklistItem("filters");
    }
    setFilters(nextFilters);
  };

  const handleWeightsChange = (nextWeights: SearchV2Weights) => {
    completeChecklistItem("tuning");
    setWeights(nextWeights);
  };

  const handleChecklistItemToggle = (item: string) => {
    setCompletedChecklistItems(searchV2ChecklistStorage.toggle(job.id, item));
  };

  const handleClearHiddenUsers = () => {
    searchInterests
      .filter((interest) => !interest.isInterested)
      .forEach((interest) => {
        deleteSearchInterest({ jobId: job.id, talentId: interest.talentId });
      });
  };

  const handleReviewAll = () => {
    setIsReviewOpen(true);
  };

  // Entering the mode clears the query and restores it on exit. The box is
  // seeded with the job's default keywords, and inheriting those would silently
  // filter the pile on terms nobody typed — the failure mode where an empty
  // list is indistinguishable from a broken feature.
  //
  // setFilters directly rather than handleFiltersChange: this is a mode switch,
  // not the staffer editing a filter, so it shouldn't tick the checklist.
  const handleToggleSallyMode = () => {
    if (isSallyMode) {
      setFilters((currentFilters) => ({
        ...currentFilters,
        keywords: keywordsBeforeSallyMode.current,
      }));
      setIsSallyMode(false);
      return;
    }
    keywordsBeforeSallyMode.current = filters.keywords;
    setFilters((currentFilters) => ({ ...currentFilters, keywords: [] }));
    setIsSallyMode(true);
  };

  /* Render */
  const renderResults = () => {
    if (isSallyMode) {
      return (
        <SearchV2SallyMode
          hasCustomerTopPriority={Boolean(job.topCustomerPriority?.trim())}
          isAgentActive={isAgentActive}
          jobLocation={`${job.city}, ${job.state}`}
          keywords={filters.keywords.map(({ keyword }) => keyword)}
          onLastDecisionChange={setLastDecision}
          onSearchInterestChange={handleSearchInterestChange}
          onShortlisted={recordShortlist}
          searchInterests={searchInterests}
          selectedStates={sallyStates}
        />
      );
    }
    if (filters.keywords.length === 0 && !isNoKeywordsAcknowledged) {
      return (
        <SearchV2NoKeywordsGate
          onAcknowledge={() => {
            setIsNoKeywordsAcknowledged(true);
          }}
        />
      );
    }
    return (
      <SearchV2CandidateList
        distanceMi={parseRadiusMi(radius)}
        includePastSubmitted={filters.includePastSubmitted}
        hasCustomerTopPriority={Boolean(job.topCustomerPriority?.trim())}
        includeRejected={filters.includeRejected}
        isAgentActive={isAgentActive}
        location={searchLocation}
        onLastDecisionChange={setLastDecision}
        onSearchInterestChange={handleSearchInterestChange}
        onShortlisted={recordShortlist}
        payCeiling={filters.payCeiling || undefined}
        query={filters.keywords.map(({ keyword }) => keyword).join(", ")}
        relevantTenure={filters.tenure ?? undefined}
        searchInterests={searchInterests}
        weights={weights}
        weTitle={filters.title.length ? filters.title : undefined}
      />
    );
  };

  const render = () => {
    return (
      <Column className="min-h-0 flex-1 overflow-auto px-10 pb-8 pt-8">
        <div className="grid w-full max-w-[1544px] grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1300px)_auto]">
          <Column className="min-w-0">
            <SearchV2Header
              filters={filters}
              hiddenUserCount={
                searchInterests.filter((interest) => !interest.isInterested)
                  .length
              }
              interestedCandidateCount={interestedCandidateCount}
              isSallyMode={isSallyMode}
              job={job}
              onClearHiddenUsers={handleClearHiddenUsers}
              onFiltersChange={handleFiltersChange}
              onRadiusChange={setRadius}
              onRecentSearchSelect={handleRecentSearchSelect}
              onReviewAll={handleReviewAll}
              onSallyStatesChange={setSallyStates}
              onToggleSallyMode={handleToggleSallyMode}
              onWeightsChange={handleWeightsChange}
              radius={radius}
              recentSearches={recentSearches}
              sallyStates={sallyStates}
              topCandidateCount={topCandidateCount}
              weights={weights}
            />
            {renderResults()}
          </Column>
          <Column className="sticky top-0 gap-4 self-start">
            <SearchV2ChecklistPanel
              completedItems={completedChecklistItems}
              onClearHiddenUsers={handleClearHiddenUsers}
              onItemToggle={handleChecklistItemToggle}
              longestHiddenDays={staleHidden.longestDays}
              staleHiddenUserCount={staleHidden.count}
            />
            <SearchSessionPanel
              getSessionProgress={getSessionProgress}
              goals={searchSessionGoals}
              interestCoins={searchSessionInterestCoins}
              interestCount={interestCount}
              lastDecision={lastDecision}
              session={searchSession}
              shortlists={searchSessionShortlists}
              stats={searchSessionStats}
            />
          </Column>
        </div>
        {isReviewOpen ? (
          <SearchApplicationReviewModal
            candidates={reviewCandidates}
            companyName={companyName}
            job={job}
            onHide={(talentId) => {
              handleSearchInterestChange(talentId, false, {
                searchAnalytics: { source: "search_review_modal" },
              });
            }}
            onOpenChange={setIsReviewOpen}
            onShortlisted={(talentId, shortlist) => {
              recordShortlist(shortlist);
              handleSearchInterestChange(talentId, null, {
                reason: "shortlist_cleanup",
                searchAnalytics: { source: "search_review_modal" },
              });
            }}
            open
          />
        ) : null}
      </Column>
    );
  };

  return render();
};
